/**
 * Pure helpers for classifying the current Word document's state +
 * inferring its probable doctype. Spec 29 Phase 3 follow-up.
 *
 * Used by ``useDocumentContext`` to drive a content-aware greeting
 * in Loom/Thread — "Welcome, type /draft" on a blank doc vs "I see
 * a CER here; want to [review] [update] or [draft fresh]?" on a
 * populated one.
 *
 * SRP: pure data transforms only. No React, no Office.js, no
 * fetches. Every branch is unit-testable with a plain
 * ``DocumentOutline`` literal.
 *
 * # DECISION: doctype inference is a loose heuristic on heading
 * #   text (keyword matching), not a rigorous classifier.
 * # WHY: we control neither the doc's origin nor its styling
 * #      conventions — a regulated-writing doc could legitimately
 * #      be any of CER / CSR / PSUR with overlapping vocabulary.
 * #      A strict classifier would false-negative too often; a
 * #      loose one occasionally false-positives but degrades to
 * #      ``null`` (generic has-content greeting) on ambiguity.
 * #      The greeting reads honestly in the null case, so the
 * #      cost of a bad guess is low.
 * # DATE: 2026-04-23
 */

import type { DocumentOutline } from "../services/wordWriter";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The classifier's output — what the greeting consumes to adapt
 *  its copy. Kept narrow; add fields only when a greeting branch
 *  actually reads them (YAGNI). */
export interface DocumentClassification {
  /** Observable state of the document at mount time. */
  state: "empty" | "has-content";
  /** How many heading paragraphs we saw. Drives the greeting's
   *  "I see a N-section document" copy when present. */
  headingCount: number;
  /** First 5 heading texts, in document order. Used both for
   *  doctype inference AND as a corroborating snippet in the
   *  greeting ("… starting with §1 Executive Summary, §2…"). */
  firstHeadings: ReadonlyArray<string>;
  /** Probable document type inferred from the headings, or null
   *  when no pattern matches (loose heuristic — see DECISION at
   *  file head). */
  probableDocType: "CER" | "CSR" | "PSUR" | null;
}

// ---------------------------------------------------------------------------
// Main classifier
// ---------------------------------------------------------------------------

/**
 * Map a Word-document outline to a classification. Empty state
 * means neither prose nor headings are present; has-content
 * means at least something is there.
 *
 * Pure; idempotent; no side-effects.
 */
export function classifyDocument(outline: DocumentOutline): DocumentClassification {
  const headingCount = outline.headings.length;
  const empty = !outline.hasAnyText && headingCount === 0;
  if (empty) {
    return {
      state: "empty",
      headingCount: 0,
      firstHeadings: [],
      probableDocType: null,
    };
  }
  const firstHeadings = outline.headings.slice(0, 5).map((h) => h.text);
  return {
    state: "has-content",
    headingCount,
    firstHeadings,
    probableDocType: inferDocType(firstHeadings),
  };
}

// ---------------------------------------------------------------------------
// Doctype heuristic — exported for tests so matching rules are pinned.
// ---------------------------------------------------------------------------

/**
 * Loose doctype inference from a set of heading titles. Returns
 * the first match in priority order, or null if nothing matches.
 *
 * Priority deliberately starts with the narrowest markers (PSUR's
 * "periodic safety update" is unambiguous) and ends with broader
 * ones (CSR's "synopsis" could appear in many docs but paired
 * with other study markers it's a strong signal).
 *
 * # DECISION: return ONE best match instead of a ranked list.
 * # WHY: the greeting picks a single doctype to address the
 * #      writer with. Ranked probabilities would force the UI to
 * #      compose "probably CER, possibly CSR" which is confusing.
 * #      When two doctypes tie or overlap ambiguously we return
 * #      null and the generic has-content greeting renders.
 * # DATE: 2026-04-23
 */
export function inferDocType(headings: ReadonlyArray<string>): DocumentClassification["probableDocType"] {
  if (headings.length === 0) return null;
  const joined = headings.join(" | ").toLowerCase();

  // PSUR — narrowest markers first. "Periodic Benefit-Risk" and
  // "Periodic Safety Update" are PSUR-specific in ICH E2C parlance.
  if (
    joined.includes("periodic benefit") ||
    joined.includes("periodic safety") ||
    joined.includes("benefit-risk evaluation")
  ) {
    return "PSUR";
  }

  // CER — EU MDR / ICH E3-adjacent markers.
  if (
    joined.includes("clinical evaluation") ||
    joined.includes("device overview") ||
    joined.includes("devices in scope")
  ) {
    return "CER";
  }

  // CSR — ICH E3 markers. "Synopsis" alone is weak; require it
  // paired with study/study-design language so we don't catch
  // generic synopses.
  const hasSynopsis = joined.includes("synopsis");
  const hasStudyLanguage =
    joined.includes("study design") || joined.includes("efficacy results") || joined.includes("safety results");
  if (hasSynopsis && hasStudyLanguage) return "CSR";
  // Explicit CSR markers without the synopsis requirement.
  if (joined.includes("clinical study report") || joined.includes("study design overview")) {
    return "CSR";
  }

  return null;
}
